import { useEffect, useRef, useState, type PointerEvent } from "react";
import { Crop, X } from "lucide-react";
import { useEscapeKey } from "../lib/useEscapeKey";

const FRAME_WIDTH = 320;
const FRAME_HEIGHT = 107;
const OUTPUT_WIDTH = 1500;
const OUTPUT_HEIGHT = 500;

/** Lets users choose the visible area of a wide profile banner before upload. */
export function BannerImageCropper({
  file,
  onCancel,
  onApply,
}: {
  file: File | null;
  onCancel: () => void;
  onApply: (image: Blob) => Promise<void>;
}) {
  const [source, setSource] = useState<string | null>(null);
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null);

  useEffect(() => {
    if (!file) {
      setSource(null);
      setNaturalSize(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setSource(objectUrl);
    setNaturalSize(null);
    setZoom(1);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  useEscapeKey(onCancel, Boolean(file) && !saving);

  const coverScale = naturalSize
    ? Math.max(FRAME_WIDTH / naturalSize.width, FRAME_HEIGHT / naturalSize.height)
    : 1;
  const scale = coverScale * zoom;
  const imageWidth = naturalSize ? naturalSize.width * scale : FRAME_WIDTH;
  const imageHeight = naturalSize ? naturalSize.height * scale : FRAME_HEIGHT;

  function clamp(x: number, y: number, nextZoom = zoom) {
    if (!naturalSize) return { x, y };
    const width = naturalSize.width * coverScale * nextZoom;
    const height = naturalSize.height * coverScale * nextZoom;
    return {
      x: Math.min(0, Math.max(FRAME_WIDTH - width, x)),
      y: Math.min(0, Math.max(FRAME_HEIGHT - height, y)),
    };
  }

  function onImageLoad() {
    const image = imageRef.current;
    if (!image) return;
    const size = { width: image.naturalWidth, height: image.naturalHeight };
    const fit = Math.max(FRAME_WIDTH / size.width, FRAME_HEIGHT / size.height);
    setNaturalSize(size);
    setPosition({
      x: (FRAME_WIDTH - size.width * fit) / 2,
      y: (FRAME_HEIGHT - size.height * fit) / 2,
    });
  }

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    dragRef.current = { startX: event.clientX, startY: event.clientY, originX: position.x, originY: position.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag) return;
    setPosition(clamp(drag.originX + event.clientX - drag.startX, drag.originY + event.clientY - drag.startY));
  }

  function onZoomChange(nextZoom: number) {
    setZoom(nextZoom);
    setPosition((current) => clamp(current.x, current.y, nextZoom));
  }

  function applyCrop() {
    const image = imageRef.current;
    if (!image || !naturalSize) return;
    setSaving(true);
    const sourceX = -position.x / scale;
    const sourceY = -position.y / scale;
    const sourceWidth = FRAME_WIDTH / scale;
    const sourceHeight = FRAME_HEIGHT / scale;
    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT_WIDTH;
    canvas.height = OUTPUT_HEIGHT;
    const context = canvas.getContext("2d");
    if (!context) {
      setSaving(false);
      return;
    }
    context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
    canvas.toBlob((blob) => {
      if (!blob) {
        setSaving(false);
        return;
      }
      void onApply(blob).catch(() => setSaveError("The cover image could not be saved. Please try again.")).finally(() => setSaving(false));
    }, "image/jpeg", 0.9);
  }

  if (!file || !source) return null;

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-navy/60 p-4" onClick={onCancel}>
      <section role="dialog" aria-modal="true" aria-labelledby="banner-crop-title" className="w-full max-w-lg rounded-xl bg-white p-5 shadow-lift" onClick={(event) => event.stopPropagation()}>
        <div className="mb-2 flex items-center gap-2"><Crop className="h-4 w-4 text-navy" /><h2 id="banner-crop-title" className="font-bold text-navy">Adjust cover image</h2></div>
        <p className="mb-4 text-sm text-mut">Drag the image to choose what appears in your profile cover. Use the slider to zoom.</p>
        <div className="mx-auto w-full max-w-[320px] overflow-hidden rounded-lg bg-navy touch-none select-none" style={{ aspectRatio: `${FRAME_WIDTH} / ${FRAME_HEIGHT}` }} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={() => { dragRef.current = null; }} onPointerCancel={() => { dragRef.current = null; }}>
          <div className="relative h-full w-full">
            <img ref={imageRef} src={source} alt="Cover image crop preview" onLoad={onImageLoad} draggable={false} style={{ position: "absolute", left: position.x, top: position.y, width: imageWidth, height: imageHeight, maxWidth: "none" }} />
            <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/50" />
          </div>
        </div>
        <label className="mt-4 mb-1 block text-sm font-semibold text-navy" htmlFor="banner-image-zoom">Zoom</label>
        <input id="banner-image-zoom" type="range" min="1" max="3" step="0.01" value={zoom} onChange={(event) => onZoomChange(Number(event.currentTarget.value))} className="w-full" />
        {saveError && <p role="alert" className="mt-2 text-sm text-brand-red">{saveError}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={saving} className="btn-outline h-9 px-4"><X className="h-4 w-4" />Cancel</button>
          <button type="button" onClick={applyCrop} disabled={saving || !naturalSize} className="btn-red h-9 px-4 disabled:opacity-60">{saving ? "Saving…" : "Use cover image"}</button>
        </div>
      </section>
    </div>
  );
}
